import { useEffect, useState } from "react";

/**
 * Tracks whether a given CSS media query currently matches.
 *
 * Useful for JS-driven responsive breakouts where rendering a different
 * component tree (e.g. a dropdown instead of a row of buttons) is cleaner
 * than toggling visibility with CSS.
 *
 * @param query A CSS media query string, e.g. "(max-width: 1336px)".
 * @returns `true` when the query matches the current viewport.
 */
export const useMediaQuery = (query: string): boolean => {
  const getMatches = (): boolean => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean>(getMatches);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => setMatches(event.matches);

    // Sync immediately in case the viewport changed between render and effect.
    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener("change", handleChange);

    return () => mediaQueryList.removeEventListener("change", handleChange);
  }, [query]);

  return matches;
};

export default useMediaQuery;
